// src/context/JobProgressContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import AuthContext from './AuthContext';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const JobProgressContext = createContext();

export const JobProgressProvider = ({ children }) => {
    const { token, user } = useContext(AuthContext);
    const [jobStatus, setJobStatus] = useState({
        loading: true,
        hasIncompleteJobs: false,
        job: null,
    });
    const [showLiveLoader, setShowLiveLoader] = useState(false);
    
    const intervalRef = useRef(null);
    const wasIncompleteRef = useRef(false);
    const isPollingRef = useRef(false);

    // Single polling function
    const checkJobProgress = useCallback(async () => {
        if (!token || !user) {
            setJobStatus({ loading: false, hasIncompleteJobs: false, job: null });
            return null;
        }

        try {
            const response = await axios.get(`${API_BASE_URL}/content/check-progress`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const job = response.data.job;

            // Check if there is an active job that's not completed
            const hasIncomplete = job &&
                job.status?.toLowerCase() !== 'completed' &&
                job.status?.toLowerCase() !== 'failed' &&
                job.status?.toLowerCase() !== 'error';

            setJobStatus({
                loading: false,
                hasIncompleteJobs: hasIncomplete,
                job: job,
            });

            // Auto-hide loader when job completes
            if (wasIncompleteRef.current && !hasIncomplete) {
                setShowLiveLoader(false);
                // Stop polling when job completes
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    isPollingRef.current = false;
                }
            }

            wasIncompleteRef.current = hasIncomplete;

            return { hasIncomplete, job };
        } catch (error) {
            console.error('Error checking job progress:', error);
            setJobStatus({ loading: false, hasIncompleteJobs: false, job: null });
            return null;
        }
    }, [token, user]);

    // Function to start polling
    const startPolling = useCallback(() => {
        // Don't start if already polling
        if (isPollingRef.current) {
            console.log('Already polling, skipping...');
            return;
        }

        console.log('Starting job progress polling...');
        isPollingRef.current = true;

        // Clear any existing interval just in case
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        // Initial check
        checkJobProgress();

        // Poll every 3 seconds
        intervalRef.current = setInterval(() => {
            checkJobProgress();
        }, 3000);
    }, [checkJobProgress]);

    // Function to stop polling
    const stopPolling = useCallback(() => {
        console.log('Stopping job progress polling...');
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        isPollingRef.current = false;
    }, []);

    // Initial check on mount to see if there are any incomplete jobs
    useEffect(() => {
        if (token && user) {
            checkJobProgress().then(result => {
                if (result && result.hasIncomplete) {
                    // If there's an incomplete job on mount, start polling
                    setShowLiveLoader(true);
                    startPolling();
                }
            });
        }

        return () => {
            stopPolling();
        };
        // eslint-disable-next-line
    }, [token, user]);

    // Monitor showLiveLoader to control polling
    useEffect(() => {
        if (showLiveLoader && !isPollingRef.current) {
            startPolling();
        } else if (!showLiveLoader && isPollingRef.current) {
            stopPolling();
        }
    }, [showLiveLoader, startPolling, stopPolling]);

    // Function to trigger when file upload completes
    const triggerJobCheck = useCallback(async () => {
        console.log('Triggering job check after upload...');
        setShowLiveLoader(true);
        // The useEffect above will handle starting the polling
    }, []);

    // Manual hide loader (if needed)
    const hideLiveLoader = useCallback(() => {
        setShowLiveLoader(false);
        stopPolling();
    }, [stopPolling]);

    const value = {
        jobStatus,
        showLiveLoader,
        setShowLiveLoader,
        triggerJobCheck,
        hideLiveLoader,
        checkJobProgress, // Expose for manual checks if needed
    };

    return (
        <JobProgressContext.Provider value={value}>
            {children}
        </JobProgressContext.Provider>
    );
};

export default JobProgressContext;